import * as THREE from 'three';

export type BrickKind = 'oneshot' | 'loop';

export type BrickDef = {
  id: number;
  x: number;
  z: number;
  width: number;
  depth: number;
  height: number;
  kind: BrickKind;
  sampleId: string;
  sampleName: string;
  hits: number;
  color: string;
  emissive: string;
};

export class Brick {
  readonly mesh: THREE.Mesh;
  readonly halfWidth: number;
  readonly halfDepth: number;
  readonly halfHeight: number;
  readonly id: number;
  readonly kind: BrickKind;
  readonly sampleId: string;
  readonly sampleName: string;
  hitsRemaining: number;
  alive = true;
  loopActive = false;
  /** 0..1 hit-flash amount; boosted to 1 on hit, decayed each frame in updateVisual. */
  flashAmount = 0;
  /** Which 16th-note step this brick arms when hit. Set by Game after construction. */
  sequencerStep = 0;
  /** Which sequencer row (= instrument row) this brick belongs to. */
  row = 0;
  /** Playback rate assigned at spawn — gives this brick a musical pitch within its family. */
  rate = 1;
  /** True while the destroy() → white-flash-fade animation is running. The
   *  same flashAmount decay drives both the emissive lerp and the opacity
   *  fade, so no separate timer is needed. */
  private isDying = false;
  /** Base color/emissive stored at construction so the dying flash can lerp
   *  toward white without permanently overwriting the original palette. */
  private readonly baseColorHex: number;
  private readonly baseEmissiveHex: number;
  private readonly baseEmissiveIntensity: number;
  /** Scratch colors reused each frame for the hit-flash lerp so we don't
   *  allocate a new THREE.Color every tick. */
  private readonly baseEmissive = new THREE.Color();
  private readonly scratchEmissive = new THREE.Color();
  private static readonly FLASH_WHITE = new THREE.Color(0xffffff);

  constructor(def: BrickDef) {
    this.id = def.id;
    this.kind = def.kind;
    this.sampleId = def.sampleId;
    this.sampleName = def.sampleName;
    this.hitsRemaining = def.hits;
    this.halfWidth = def.width / 2;
    this.halfDepth = def.depth / 2;
    this.halfHeight = def.height / 2;

    // No inset — bricks render flush edge-to-edge so adjacent bricks form a
    // seamless wall (matches the no-grid look).
    const geometry = new THREE.BoxGeometry(def.width, def.height, def.depth);
    // Bake a 3-axis "soft light from top-right" gradient into the vertices.
    // Weighted blend of X (right), Y (top), Z (front) factors so the top-right
    // corner reads as a subtle highlight and the opposite corner as a shadow.
    // The weights bias the visible peak toward the crown/right (which the
    // top-down camera actually sees) without leaving the side faces flat.
    const posAttr = geometry.getAttribute('position');
    const colorArr = new Float32Array(posAttr.count * 3);
    for (let i = 0; i < posAttr.count; i++) {
      const x = posAttr.getX(i);
      const y = posAttr.getY(i);
      const z = posAttr.getZ(i);
      const tX = (x + def.width / 2) / def.width;    // 0 left → 1 right
      const tY = (y + def.height / 2) / def.height;  // 0 bottom → 1 top
      const tZ = (z + def.depth / 2) / def.depth;    // 0 back → 1 front
      // Directional light dot: emphasize top most, right second, front third.
      const lit = tX * 0.30 + tY * 0.55 + tZ * 0.15;
      // Map 0..1 range to 0.78 → 1.22 for a subtle ±22% brightness delta.
      const shade = 0.78 + lit * 0.44;
      colorArr[i * 3 + 0] = shade;
      colorArr[i * 3 + 1] = shade;
      colorArr[i * 3 + 2] = shade;
    }
    geometry.setAttribute('color', new THREE.BufferAttribute(colorArr, 3));
    const material = new THREE.MeshStandardMaterial({
      color: def.color,
      roughness: 0.42,
      metalness: 0.18,
      emissive: def.emissive,
      emissiveIntensity: this.kind === 'loop' ? 0.55 : 0.28,
      vertexColors: true,
      // Transparent from the start (opacity 1) so the destroy-fade doesn't
      // shift the mesh into a different render pass mid-animation and cause
      // a visible pop at the transition point.
      transparent: true,
    });
    this.baseColorHex = material.color.getHex();
    this.baseEmissiveHex = material.emissive.getHex();
    this.baseEmissive.copy(material.emissive);
    this.baseEmissiveIntensity = this.kind === 'loop' ? 0.55 : 0.28;
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.mesh.position.set(def.x, def.height / 2, def.z);

    if (this.kind === 'loop') {
      const rim = new THREE.Mesh(
        new THREE.BoxGeometry(def.width * 0.98, 0.04, def.depth * 0.92),
        new THREE.MeshBasicMaterial({ color: '#7cf0c2' }),
      );
      rim.position.y = def.height / 2 + 0.02;
      this.mesh.add(rim);
    }
  }

  get x(): number {
    return this.mesh.position.x;
  }

  get z(): number {
    return this.mesh.position.z;
  }

  advanceZ(step: number): void {
    if (!this.alive) return;
    this.mesh.position.z += step;
  }

  hit(): 'destroyed' | 'damaged' | 'toggled' {
    if (!this.alive) return 'destroyed';
    this.flashAmount = 1;

    if (this.kind === 'loop') {
      this.loopActive = !this.loopActive;
      this.hitsRemaining -= 1;
      if (this.hitsRemaining <= 0) {
        this.destroy();
        return 'destroyed';
      }
      return 'toggled';
    }

    this.hitsRemaining -= 1;
    if (this.hitsRemaining <= 0) {
      this.destroy();
      return 'destroyed';
    }
    return 'damaged';
  }

  destroy(): void {
    if (this.isDying) return;
    this.alive = false;
    this.loopActive = false;
    // Ride the same white-flash pulse as damage hits — the flashAmount decay
    // in updateVisual drives both the emissive lerp AND the opacity fade,
    // so a destroyed brick reads as "hit hard, then dissolves" instead of
    // running a separate destroy animation.
    this.isDying = true;
    this.flashAmount = 1;
    const mat = this.mesh.material as THREE.MeshStandardMaterial;
    mat.depthWrite = false;
  }

  reset(hits: number): void {
    this.alive = true;
    this.loopActive = false;
    this.isDying = false;
    this.hitsRemaining = hits;
    this.flashAmount = 0;
    this.mesh.visible = true;
    this.mesh.scale.set(1, 1, 1);
    const mat = this.mesh.material as THREE.MeshStandardMaterial;
    mat.color.setHex(this.baseColorHex);
    mat.emissive.setHex(this.baseEmissiveHex);
    mat.emissiveIntensity = this.baseEmissiveIntensity;
    mat.opacity = 1;
    mat.depthWrite = true;
  }

  updateVisual(elapsed: number): void {
    if (!this.alive && !this.isDying) return;
    const mat = this.mesh.material as THREE.MeshStandardMaterial;
    if (this.kind === 'loop' && this.loopActive) {
      mat.emissiveIntensity = 0.75 + Math.sin(elapsed * 8 + this.id) * 0.35;
    }
    // Hit flash: same treatment as the paddle — lerp the emissive color
    // toward white (row emissives are dark, so a bare intensity boost read
    // almost nothing) and spike the intensity together. Decays each frame
    // back to the base emissive so the pulse feels like a punch of light.
    if (this.flashAmount > 0.001) {
      const base = this.kind === 'loop' && this.loopActive
        ? mat.emissiveIntensity
        : this.baseEmissiveIntensity;
      this.scratchEmissive.copy(this.baseEmissive).lerp(Brick.FLASH_WHITE, this.flashAmount);
      mat.emissive.copy(this.scratchEmissive);
      mat.emissiveIntensity = base + this.flashAmount * 3.5;
      // For a destroyed brick, ride the same decay to also fade opacity out.
      // The brick disappears in lockstep with the flash — no separate animation.
      if (this.isDying) {
        mat.opacity = this.flashAmount;
      }
      this.flashAmount *= 0.86; // ~150ms decay at 60fps
    } else if (this.isDying) {
      // Flash has fully decayed on a dying brick — hide the mesh cleanly.
      this.isDying = false;
      this.mesh.visible = false;
      mat.opacity = 1;
      mat.emissiveIntensity = this.baseEmissiveIntensity;
      mat.emissive.copy(this.baseEmissive);
      mat.depthWrite = true;
    } else if (this.kind !== 'loop' || !this.loopActive) {
      mat.emissive.copy(this.baseEmissive);
      mat.emissiveIntensity = this.baseEmissiveIntensity;
    }
  }

dispose(): void {
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        const material = child.material;
        if (Array.isArray(material)) material.forEach((m) => m.dispose());
        else material.dispose();
      }
    });
  }
}
